import React, { Component } from "react"
import { clamp } from "../../services/utils/math-util"
import { getElementLayoutInfo } from "../../services/utils/html-util"
import "./index.css"

const DESMOS_URL = "https://www.desmos.com/api/v1.11.4/calculator.js?apiKey=9c830f6f518f4753a31ee02a7a5162cf"

declare global {
    interface Window {
        Desmos: any
    }
}

export type CalculatorConfig = "ACT" | "SAT"

interface Props {
    title?: string
    draggable?: boolean
    calculatorClass?: string
    onClose?: () => void
    config?: CalculatorConfig //"ACT" | "SAT"
    startMaximized?: boolean
    showMaximizeButton?: boolean
    showCenterIcon?: boolean
    resizable?: boolean
}

interface State {
    scriptLoaded: boolean
    maximized: boolean
}

const CONFIG_ACT = {
    // Common for ACT & SAT
    folders: false,
    images: false,
    links: false,
    notes: false,

    // ACT only
    capExpressionSize: true,
    clearIntoDegreeMode: true,
    degreeMode: true,
    forceEnableGeometryFunctions: true,
    qwertyKeyboard: true,
    restrictedFunctions: true,
}

const CONFIG_SAT = {
    // Common for ACT & SAT
    folders: false,
    images: false,
    links: false,
    notes: false,

    // SAT only
    defaultLogModeRegressions: true,
}

/**
 * This calculator uses the embeddable Desmos calculator that we licenced from Desmos Studio
 */
export default class Calculator extends Component<Props, State> {
    containerRef
    calculatorRef
    calculator

    constructor(props) {
        super(props)
        this.containerRef = React.createRef()
        this.calculatorRef = React.createRef()
        this.calculator = null
        this.state = {
            scriptLoaded: false,
            maximized: false,
        }
    }

    componentDidMount() {
        let {draggable=true, startMaximized=false} = this.props

        this.loadScript()

        if (draggable) {
            this.enableDragging()
        }

        if (startMaximized) {
            this.setState({maximized: true})
        }

        window.addEventListener("resize", this.handleWindowResize)
    }

    componentWillUnmount() {
        if (this.calculator) {
            this.calculator.destroy()
        }
        window.removeEventListener("resize", this.handleWindowResize)
    }

    loadScript = () => {
        let existingScript = document.querySelector(`script[src="${DESMOS_URL}"]`)

        if (existingScript) {
            if (window.Desmos) {
                this.setState({ scriptLoaded: true }, this.init)
            }
            else {
                existingScript.addEventListener("load", () => {
                    this.setState({ scriptLoaded: true }, this.init)
                })
            }
        }
        else {
            let script = document.createElement("script")
            script.src = DESMOS_URL
            script.async = true
            script.onload = () => {
                this.setState({ scriptLoaded: true }, this.init)
            }
            document.body.appendChild(script)
        }
    }

    init = () => {
        let {config="SAT"} = this.props
        let desmosConfig = config === "ACT" ? CONFIG_ACT: CONFIG_SAT

        if (this.calculatorRef.current && window.Desmos) {
            this.calculator = window.Desmos.GraphingCalculator(this.calculatorRef.current, desmosConfig)
        }
    }

    handleWindowResize = () => {
        let container = this.containerRef.current
        let layoutContext = getElementLayoutInfo(container)

        if (layoutContext && layoutContext.parent) {
            let {width, height} = layoutContext.parent.rect
            container.style.left = `${width/2}px`
            container.style.top = `${height/2}px`
        }
    }

    enableDragging = () => {
        let container = this.containerRef.current
        let innerContainer = container?.querySelector(".calculator-top-panel") as HTMLElement

        if (!container || !innerContainer) return

        let offsetX = 0
        let offsetY = 0
        let dragging = false

        let onMouseDown = (e: MouseEvent) => {
            dragging = true
            offsetX = e.clientX - container.offsetLeft
            offsetY = e.clientY - container.offsetTop
            document.addEventListener("mousemove", onMouseMove)
            document.addEventListener("mouseup", onMouseUp)
        }

        let onMouseMove = (e: MouseEvent) => {
            if (dragging) {
                let {clientX, clientY} = e
                let layoutContext = getElementLayoutInfo(container)

                if (layoutContext?.parent) {
                    let {rect: parentRect} = layoutContext.parent
                    let padding = 30
                    let boundaries = { minX: padding, maxX: parentRect.width-padding, minY: padding, maxY: parentRect.height-padding}

                    // Enforce the boundaries!
                    clientX = clamp(clientX, boundaries.minX, boundaries.maxX)
                    clientY = clamp(clientY, boundaries.minY, boundaries.maxY)
                }

                let left = clientX - offsetX
                let top = clientY - offsetY

                container.style.left = `${left}px`
                container.style.top = `${top}px`
            }
        }

        let onMouseUp = () => {
            dragging = false
            document.removeEventListener("mousemove", onMouseMove)
            document.removeEventListener("mouseup", onMouseUp)
        }

        innerContainer.addEventListener("mousedown", onMouseDown)
    }

    handleClose = (e) => {
        e.preventDefault()
        let {onClose} = this.props
        if (onClose) {
            onClose()
        }
    }

    toggleMaximize = (e) => {
        e.preventDefault()
        let {maximized} = this.state
        this.setState({maximized: !maximized})
    }

    render() {
        let { title="Calculator", calculatorClass="", config="SAT", showMaximizeButton=true, showCenterIcon=true, resizable=false } = this.props
        let { maximized } = this.state
        let extraClass = `${resizable ? "resizable-calculator" : ""} ${config === "SAT" ? "calculator__sat" : ""}`

        return (
            <div ref={this.containerRef} className={`calculator-container ${maximized ? "wider" : ""} ${calculatorClass} ${extraClass}`}>
                <div className={`calculator-top-panel`}>
                    <div className="calculator-title">
                        {title}
                    </div>

                    { showCenterIcon &&
                        <div className="calculator-center">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" 
                                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="12" cy="9" r="1"/>
                                    <circle cx="19" cy="9" r="1"/>
                                    <circle cx="5" cy="9" r="1"/>
                                    <circle cx="12" cy="15" r="1"/>
                                    <circle cx="19" cy="15" r="1"/>
                                    <circle cx="5" cy="15" r="1"/>
                            </svg>
                        </div>
                    }

                    <div className="calculator-controls">
                        { showMaximizeButton &&
                            <button className="simple-btn btnToggleSize" onClick={this.toggleMaximize} title="Toggle Calculator Size">
                                {!maximized ?
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" 
                                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" >
                                        <path d="M15 3h6v6"/>
                                        <path d="m21 3-7 7"/><path d="m3 21 7-7"/>
                                        <path d="M9 21H3v-6"/>
                                    </svg>
                                    :
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" 
                                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                        <path d="m14 10 7-7"/><path d="M20 10h-6V4"/>
                                        <path d="m3 21 7-7"/>
                                        <path d="M4 14h6v6"/>
                                    </svg>
                                }
                            </button>
                        }
                        <button className="simple-btn btnClose" onClick={this.handleClose} title="Close Calculator">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" 
                                stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                <path d="M18 6 6 18"/>
                                <path d="m6 6 12 12"/>
                            </svg>
                        </button>
                    </div>
                </div>
                <div ref={this.calculatorRef} className="calculator" />
            </div>
        )
    }
}
